
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { User } from "lucide-react";

export default function CreateCustomerModal({ isOpen, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    shipping_address: {
      street: '',
      city: '',
      state: '',
      postal_code: '',
      country: ''
    },
    billing_address: {
      street: '',
      city: '',
      state: '',
      postal_code: '',
      country: ''
    },
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [useSameAddress, setUseSameAddress] = useState(true);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressChange = (addressType, field, value) => {
    setFormData(prev => ({
      ...prev,
      [addressType]: {
        ...prev[addressType],
        [field]: value
      }
    }));

    // If using same address, sync billing with shipping
    if (useSameAddress && addressType === 'shipping_address') {
      setFormData(prev => ({
        ...prev,
        billing_address: {
          ...prev.billing_address,
          [field]: value
        }
      }));
    }
  };

  const handleSameAddressToggle = (checked) => {
    setUseSameAddress(checked);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        billing_address: { ...prev.shipping_address }
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await onSubmit(formData);
      
      // Reset form
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        shipping_address: {
          street: '',
          city: '',
          state: '',
          postal_code: '',
          country: ''
        },
        billing_address: {
          street: '',
          city: '',
          state: '',
          postal_code: '',
          country: ''
        },
        notes: ''
      });
      setUseSameAddress(true);
      onClose();
    } catch (error) {
      console.error('Error creating customer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-amber-600" />
            {t('customers.create.title')}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900">
              {t('customers.create.basic_info')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full_name">{t('customers.create.full_name')}</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                  placeholder={t('placeholders.auth.full_name')}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="email">{t('customers.create.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder={t('placeholders.auth.email')}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="phone">{t('customers.create.phone')}</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder={t('placeholders.auth.phone')}
                />
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900">
              {t('customers.create.shipping_address')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="shipping_street">{t('customers.create.street')}</Label>
                <Input
                  id="shipping_street"
                  value={formData.shipping_address.street}
                  onChange={(e) => handleAddressChange('shipping_address', 'street', e.target.value)}
                  placeholder={t('placeholders.auth.address')}
                />
              </div>
              
              <div>
                <Label htmlFor="shipping_city">{t('customers.create.city')}</Label>
                <Input
                  id="shipping_city"
                  value={formData.shipping_address.city}
                  onChange={(e) => handleAddressChange('shipping_address', 'city', e.target.value)}
                  placeholder={t('placeholders.auth.city')}
                />
              </div>
              
              <div>
                <Label htmlFor="shipping_state">{t('customers.create.state')}</Label>
                <Input
                  id="shipping_state"
                  value={formData.shipping_address.state}
                  onChange={(e) => handleAddressChange('shipping_address', 'state', e.target.value)}
                  placeholder={t('placeholders.auth.state')}
                />
              </div>
              
              <div>
                <Label htmlFor="shipping_postal_code">{t('customers.create.postal_code')}</Label>
                <Input
                  id="shipping_postal_code"
                  value={formData.shipping_address.postal_code}
                  onChange={(e) => handleAddressChange('shipping_address', 'postal_code', e.target.value)}
                  placeholder={t('placeholders.auth.postal_code')}
                />
              </div>
              
              <div>
                <Label htmlFor="shipping_country">{t('customers.create.country')}</Label>
                <Input
                  id="shipping_country"
                  value={formData.shipping_address.country}
                  onChange={(e) => handleAddressChange('shipping_address', 'country', e.target.value)}
                  placeholder={t('placeholders.auth.country')}
                />
              </div>
            </div>
          </div>

          {/* Billing Address */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">
                {t('customers.create.billing_address')}
              </h3>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="useSameAddress"
                  checked={useSameAddress}
                  onChange={(e) => handleSameAddressToggle(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="useSameAddress" className="text-sm">
                  {t('customers.create.use_same_address')}
                </Label>
              </div>
            </div>
            
            {!useSameAddress && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="billing_street">{t('customers.create.street')}</Label>
                  <Input
                    id="billing_street"
                    value={formData.billing_address.street}
                    onChange={(e) => handleAddressChange('billing_address', 'street', e.target.value)}
                    placeholder={t('placeholders.auth.address')}
                  />
                </div>
                
                <div>
                  <Label htmlFor="billing_city">{t('customers.create.city')}</Label>
                  <Input
                    id="billing_city"
                    value={formData.billing_address.city}
                    onChange={(e) => handleAddressChange('billing_address', 'city', e.target.value)}
                    placeholder={t('placeholders.auth.city')}
                  />
                </div>
                
                <div>
                  <Label htmlFor="billing_state">{t('customers.create.state')}</Label>
                  <Input
                    id="billing_state"
                    value={formData.billing_address.state}
                    onChange={(e) => handleAddressChange('billing_address', 'state', e.target.value)}
                    placeholder={t('placeholders.auth.state')}
                  />
                </div>
                
                <div>
                  <Label htmlFor="billing_postal_code">{t('customers.create.postal_code')}</Label>
                  <Input
                    id="billing_postal_code"
                    value={formData.billing_address.postal_code}
                    onChange={(e) => handleAddressChange('billing_address', 'postal_code', e.target.value)}
                    placeholder={t('placeholders.auth.postal_code')}
                  />
                </div>
                
                <div>
                  <Label htmlFor="billing_country">{t('customers.create.country')}</Label>
                  <Input
                    id="billing_country"
                    value={formData.billing_address.country}
                    onChange={(e) => handleAddressChange('billing_address', 'country', e.target.value)}
                    placeholder={t('placeholders.auth.country')}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">{t('customers.create.notes')}</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder={t('placeholders.auth.notes')}
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t('actions.cancel')}
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
            >
              {isSubmitting ? t('actions.creating') : t('customers.create.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
